import { pdfjs } from 'react-pdf';
import { v4 as uuidv4 } from 'uuid';
import {
  PDF_WORKER_SRC,
  MAX_PDF_WORDS,
  MAX_TOTAL_ALLOWED_MB_LIMIT,
} from 'components/AiMode/constants';
import { useEffect, useState } from 'react';
import { PdfFileType, ImageType } from 'types';
import { useAppContext } from 'context/AppContext';

pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;

export const useUploadFiles = () => {
  const { setPopUpMessageType } = useAppContext();

  const [userPdfAndImages, setUserPdfAndImages] = useState<{
    pdfs: PdfFileType[];
    images: ImageType[];
  }>({
    pdfs: [] as PdfFileType[],
    images: [] as ImageType[],
  });
  const updatePdfFiles = (pdfs: PdfFileType[]) => {
    setUserPdfAndImages((prev) => ({ ...prev, pdfs }));
  };
  const updateImages = (images: ImageType[]) => {
    setUserPdfAndImages((prev) => ({ ...prev, images }));
  };

  useEffect(() => {
    const isPdfOrImagesPresent =
      userPdfAndImages.pdfs.length > 0 || userPdfAndImages.images.length > 0;
    const previewItemHeight = isPdfOrImagesPresent ? '60px' : '0px';
    document.documentElement.style.setProperty('--preview-item-height', previewItemHeight);
  }, [userPdfAndImages]);

  const resetUserPdfAndImages = () => {
    setUserPdfAndImages({
      pdfs: [] as PdfFileType[],
      images: [] as ImageType[],
    });
  };

  async function extractPdfText(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item: any) => item.str).join(' ');
      fullText += pageText + '\n';
    }

    return fullText.trim();
  }

  function applyWordLimit(pdfs: PdfFileType[]): PdfFileType[] {
    let totalWords = 0;
    const result: PdfFileType[] = [];

    for (const pdf of pdfs) {
      const words = pdf.text.split(/\s+/).filter(Boolean);

      if (totalWords >= MAX_PDF_WORDS) {
        result.push({
          ...pdf,
          text: '',
          wordCount: words.length,
          size: pdf.size,
          id: pdf.id,
        });
      } else {
        const remaining = MAX_PDF_WORDS - totalWords;
        if (words.length <= remaining) {
          totalWords += words.length;
          result.push({ ...pdf, wordCount: words.length });
        } else {
          const truncatedText = words.slice(0, remaining).join(' ');
          totalWords += remaining;
          result.push({
            ...pdf,
            text: truncatedText,
            wordCount: remaining,
            size: pdf.size,
            id: pdf.id,
          });
        }
      }
    }

    return result;
  }

  function applyTotalPDFLimitByMB(
    pdfs: PdfFileType[],
    totalSize: number
  ): {
    result: PdfFileType[];
    isExceeded: boolean;
    totalSize: number;
  } {
    let isExceeded = false;
    const result: PdfFileType[] = [];

    for (const pdf of pdfs) {
      totalSize += parseFloat(pdf.size);
      if (totalSize > MAX_TOTAL_ALLOWED_MB_LIMIT) {
        isExceeded = true;
      } else {
        result.push(pdf);
      }
    }
    return { result, isExceeded, totalSize };
  }

  function applyTotalImageLimitByMB(
    images: ImageType[],
    totalSize: number
  ): {
    result: ImageType[];
    isExceeded: boolean;
    totalSize: number;
  } {
    let isExceeded = false;
    const result: ImageType[] = [];

    for (const image of images) {
      totalSize += parseFloat(image.size);
      if (totalSize > MAX_TOTAL_ALLOWED_MB_LIMIT) {
        isExceeded = true;
      } else {
        result.push(image);
      }
    }
    return { result, isExceeded, totalSize };
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      const newPdfs: PdfFileType[] = [];
      const newImages: ImageType[] = [];

      const filePromises = Array.from(files).map((file) => {
        return new Promise<void>((resolve) => {
          const id = uuidv4();
          if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
              newImages.push({
                name: file.name,
                size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
                content: reader.result as string,
                id,
              });
              resolve();
            };
            reader.readAsDataURL(file);
          } else {
            extractPdfText(file)
              .then((text) => {
                const wordCount = text.split(/\s+/).filter(Boolean).length;
                newPdfs.push({
                  name: file.name,
                  text,
                  wordCount,
                  size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
                  id,
                });
                resolve();
              })
              .catch(() => resolve());
          }
        });
      });
      await Promise.all(filePromises);

      const updatedImages = [...userPdfAndImages.images, ...newImages];
      const {
        result: limitedImages,
        isExceeded: isImagesExceeded,
        totalSize: updatedTotalSize,
      } = applyTotalImageLimitByMB(updatedImages, 0);
      updateImages(limitedImages);

      const allPdfs = [...userPdfAndImages.pdfs, ...newPdfs];
      const { result: limitedPdfs, isExceeded: isPdfsExceeded } = applyTotalPDFLimitByMB(
        allPdfs,
        updatedTotalSize
      );
      updatePdfFiles(limitedPdfs);
      if (isImagesExceeded || isPdfsExceeded) {
        setPopUpMessageType('FAILED_UPLOAD');
      }
    } catch (err) {
      console.error('PDF processing error:', err);
      setPopUpMessageType('FAILED_UPLOAD');
    } finally {
      e.target.value = '';
    }
  };

  const handleDelete = (id: string, type: 'pdf' | 'image') => {
    if (type === 'pdf') {
      const filteredPdfs = userPdfAndImages.pdfs.filter((pdf) => pdf.id !== id);
      const { result: limitedPdfs } = applyTotalPDFLimitByMB(filteredPdfs, 0);
      updatePdfFiles(limitedPdfs);
    } else {
      const filteredImages = userPdfAndImages.images.filter((img) => img.id !== id);
      const { result: limitedImages } = applyTotalImageLimitByMB(filteredImages, 0);
      updateImages(limitedImages);
    }
  };

  return {
    handleUpload,
    handleDelete,
    applyWordLimit,
    userPdfAndImages,
    resetUserPdfAndImages,
  };
};
